import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useQuickCreateCommunicationOptional } from 'components/drawers/quick-create-communication-drawer/quick-create-communication.context';
import { useQuickCreateLocationContext } from 'components/drawers/quick-create-communication-drawer/quick-create-communication.location';
import { daysSinceLowerBound } from 'components/drawers/quick-create-communication-drawer/quick-create-communication.utils';
import { useTranslation } from 'react-i18next';
import ConversionByVisitChart from './conversion-by-visit-chart.component';
import { RetentionVisitViewRow } from './retention.utils';

export type RetentionConversionProps = {
  visits?: RetentionVisitViewRow[];
  criticalVisits?: number[];
  'data-testid'?: string;
};

/**
 * Render the critical visit numbers as a compact range ("2–4") when contiguous,
 * otherwise a comma list ("2, 5, 9").
 */
export const formatCriticalRange = (visits: number[]): string => {
  if (visits.length === 0) return '';
  const sorted = [...visits].sort((a, b) => a - b);
  const contiguous = sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
  if (contiguous && sorted.length > 1) {
    return `${sorted[0]}–${sorted[sorted.length - 1]}`;
  }
  return sorted.join(', ');
};

const RetentionConversion = ({
  visits = [],
  criticalVisits = [],
  'data-testid': dataTestId = 'Retention.Conversion',
}: RetentionConversionProps) => {
  const { t } = useTranslation();
  const quickCreate = useQuickCreateCommunicationOptional();
  const location = useQuickCreateLocationContext();

  const onBarAction = quickCreate
    ? (row: RetentionVisitViewRow) =>
        quickCreate.openQuickCreateCommunication({
          kind: 'retention',
          visit: row.visitNumber,
          lifetimeVisits: row.visitNumber - 1,
          medianDaysSinceLast: row.medianDaysFromLastVisit,
          daysSinceLastLowerBound: daysSinceLowerBound(
            row.medianDaysFromLastVisit,
          ),
          ...location,
        })
    : undefined;

  return (
    <Card data-testid={dataTestId}>
      <CardContent>
        <Typography variant="body2" component="div" marginBottom={1}>
          {t('page.guestJourney.retention.conversionChart.title')}
        </Typography>

        <ConversionByVisitChart data={visits} onBarAction={onBarAction} />

        {criticalVisits.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            marginTop={1}
            data-testid={`${dataTestId}.Callout`}
          >
            <PriorityHighIcon fontSize="small" color="error" />
            <Typography variant="body2" color="text.secondary">
              {t('page.guestJourney.retention.conversionChart.callout', {
                visits: formatCriticalRange(criticalVisits),
              })}
              {onBarAction &&
                ` ${t(
                  'page.guestJourney.retention.conversionChart.actionHint',
                )}`}
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RetentionConversion;
